import { formatDistanceToNow } from "date-fns";

import { trackEvent } from "@needl-ai/common/analytics";

import ListItem from "./ListItem";
import SectionHeader from "./SectionHeader";
import SessionsEmptyState from "./SessionsEmptyState";
import SessionListSkeleton from "./SessionSkeleton";

import type { AsyncAnswerStatus } from "@/hooks/useAsyncSessionTracker";
import type { SessionInfo } from "@/hooks/useCreateUpdateSession";

type ChatSessionsListProps = {
  sessions: SessionInfo[];
  sessionsLoaded: boolean;
  sessionId?: string;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string) => void;
  isLoading?: boolean;
  isStreaming?: boolean;
  hasIncompleteAttachments?: boolean;
  sectionTitle?: string;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  asyncStatuses?: Map<string, AsyncAnswerStatus>;
};

const ChatSessionsList = ({
  sessions,
  sessionsLoaded,
  sessionId,
  onSelectSession,
  onDeleteSession,
  isLoading = false,
  isStreaming = false,
  hasIncompleteAttachments = false,
  sectionTitle = "Recent Chats",
  emptyStateTitle = "No conversations yet",
  emptyStateSubtitle = "Ask Anything to begin",
  asyncStatuses,
}: ChatSessionsListProps) => {
  // Format session date to relative time (like "2 minutes ago")
  const formatSessionDate = (date: Date): string => {
    return formatDistanceToNow(date, { addSuffix: true });
  };

  return (
    <>
      <div className="px-4">
        <div className="flex items-center justify-between mb-3">
          <SectionHeader title={sectionTitle} />
          <div className="flex-grow border-b border-gray-300 dark:border-neutral-700 ml-2"></div>
        </div>

        <div className="space-y-1">
          {!sessionsLoaded ? (
            <SessionListSkeleton count={5} />
          ) : sessions.length === 0 ? (
            <SessionsEmptyState
              title={emptyStateTitle}
              subtitle={emptyStateSubtitle}
            />
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className={`rounded-lg transition-opacity ${
                  isLoading || isStreaming || hasIncompleteAttachments
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                } ${
                  session.id === sessionId
                    ? "bg-gray-200/80 dark:bg-neutral-800"
                    : ""
                }`}
              >
                <ListItem
                  title={session.title}
                  type="chat"
                  time={formatSessionDate(new Date(session.updated_at))}
                  isActive={session.id === sessionId}
                  disabled={
                    isLoading || isStreaming || hasIncompleteAttachments
                  }
                  asyncStatus={
                    session.id !== sessionId
                      ? asyncStatuses?.get(session.id)
                      : undefined
                  }
                  onClick={() => {
                    if (isLoading || isStreaming || hasIncompleteAttachments) {
                      return;
                    }

                    if (session.id !== sessionId) {
                      trackEvent("chat_opened", {
                        session_id: session.id,
                        query: session.title,
                      });
                      onSelectSession(session.id);
                    }
                  }}
                  onDelete={() => onDeleteSession(session.id)}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default ChatSessionsList;
